import _ from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Icon from 'react-icons-kit';
import { ic_check_box as checkboxChecked } from 'react-icons-kit/md/ic_check_box';
import { ic_check_box_outline_blank as checkboxUnchecked } from 'react-icons-kit/md/ic_check_box_outline_blank';
import { ic_indeterminate_check_box as checkboxIndeterminate } from 'react-icons-kit/md/ic_indeterminate_check_box';
import moment from 'moment';
import MessageModal from '../../components/MessageModal.js';
import DeleteEntryModal from '../../components/DeleteEntryModal.js';
import EntryOptions from '../../components/EntryOptions.js';
import NoEntriesFound from '../../components/NoEntriesFound.js';
import { figureNameOrDescription } from '../../../utils/CommonFunctions.js';
import { showToast } from '../../../actions/Actions.js';
import {
    deleteExpenseEntries,
    setExpenseEntriesBillable,
    setExpenseEntriesApproved,
    setExpenseEntriesLocked,
} from '../../../actions/ExpensesActions';
import ExpenseDetailsRow from './ExpenseDetailsRow.js';
import '../../../css/Expenses.css';

class ExpenseDetails extends Component {
    static propTypes = {
        weeklyTotals: PropTypes.object,
        weeklyGrandTotal: PropTypes.object,
        entriesByWeek: PropTypes.object.isRequired,
        entriesAvailableForEdit: PropTypes.array.isRequired,
        weekIds: PropTypes.array.isRequired,
        selectedEntryIds: PropTypes.arrayOf(PropTypes.string).isRequired,
        editExpenseEntry: PropTypes.func.isRequired,
        duplicateExpenseEntry: PropTypes.func.isRequired,
        deleteExpenseEntries: PropTypes.func.isRequired,
        startDate: PropTypes.any,
        onEntrySelect: PropTypes.func.isRequired,
        onSelectAllPress: PropTypes.func.isRequired,
        shouldShowBillable: PropTypes.bool.isRequired,
        setExpenseEntriesApproved: PropTypes.func.isRequired,
        setExpenseEntriesBillable: PropTypes.func.isRequired,
        updatingStatusEntryIds: PropTypes.array.isRequired,
        updatingBillableEntryIds: PropTypes.array.isRequired,
        noIntegration: PropTypes.bool,
    };

    static defaultProps = {
        entriesByWeek: {},
        weekIds: [],
        weeklyTotals: {},
        weeklyGrandTotal: {},
        updatingBillableEntryIds: [],
        updatingStatusEntryIds: [],
    };

    state = {
        isMessageModalOpen: false,
        isDeleteEntryModalOpen: false,
        selectedEntry: null,
    };

    handleMessageModalClosed = () => {
        this.setState({
            isMessageModalOpen: false,
        });
    };

    handleMessagePressed = entry => {
        this.setState({
            isMessageModalOpen: true,
            selectedEntry: entry,
        });
    };

    handleDeleteEntryModalClosed = () => {
        this.setState({
            isDeleteEntryModalOpen: false,
        });
    };

    handleDeletePressed = entry => {
        this.setState({
            isDeleteEntryModalOpen: true,
            selectedEntry: entry,
        });
    };

    handleDeleteConfirmed = () => {
        this.props.deleteExpenseEntries([this.state.selectedEntry.id]);
    };

    handleToggleApprovePressed = (entry, noIntegration, override) => {
        const approvedStatus = entry.approved === 'Yes' ? 'No' : 'Yes';

        const ids = [entry.id];

        if (noIntegration && entry.locked === 'Yes') {
            return;
        } else if (
            noIntegration &&
            entry.locked === 'No' &&
            entry.approved === 'Yes' &&
            !override
        ) {
            this.props.setExpenseEntriesLocked(ids);
        } else {
            entry.approved = approvedStatus;
            this.props.setExpenseEntriesApproved(ids, approvedStatus);
        }
    };

    handleBillablePressed = entry => {
        const billableStatus = entry.billable === 'Yes' ? 'No' : 'Yes';
        entry.billable = billableStatus;
        const ids = [entry.id];
        this.props.setExpenseEntriesBillable(ids, billableStatus);
    };

    rowForEntry = entry => {
        const {
            selectedEntryId,
            selectedEntryIds,
            editExpenseEntry,
            duplicateExpenseEntry,
            onEntrySelect = _.noop,
            shouldShowBillable,
            shouldShowQbClasses,
            updatingBillableEntryIds,
            updatingStatusEntryIds,
            noIntegration,
            readOnly,
        } = this.props;

        const vendor = entry.Vendor;
        const customer = entry.Customer;
        const expenseable = entry.Expenseable;
        const qbClass = entry.QbClass;
        const billObject = entry.ExpenseGrouping;

        const unknownEntities = entry.unknown_entities;
        const isAbleToSync = unknownEntities.length === 0;

        const vendorName = figureNameOrDescription(vendor),
            customerName = figureNameOrDescription(customer),
            serviceName = figureNameOrDescription(qbClass),
            expenseableName = figureNameOrDescription(expenseable),
            bill = figureNameOrDescription(billObject);

        const isBillable = entry.billable === 'Yes';
        const isSynced = entry.exported === 'Yes';
        const isApprovedForSyncing = entry.approved === 'Yes';
        const isLocked = entry.locked === 'Yes';
        const isSelected = selectedEntryIds.includes(entry.id);
        const isEditing = entry.id === selectedEntryId;
        const isUpdatingBillable = updatingBillableEntryIds.includes(entry.id);
        const isUpdatingStatus = updatingStatusEntryIds.includes(entry.id);
        const canEdit = entry.can_be_edited_by_current_user;
        const canApprove = entry.can_be_approved_by_current_user;
        const pending_override = false;
        const entryOptions = (
            <EntryOptions
                entry={entry}
                onToggleApproved={this.handleToggleApprovePressed}
                onToggleBillable={this.handleBillablePressed}
                onEditEntry={editExpenseEntry}
                onDuplicateEntry={duplicateExpenseEntry}
                onDeletePressed={this.handleDeletePressed}
                onMessagePressed={this.handleMessagePressed}
                canApprove={canApprove}
                canEdit={canEdit}
                shouldShowBillable={shouldShowBillable}
                noIntegration={noIntegration}
            />
        );

        return (
            <ExpenseDetailsRow
                key={entry.id}
                id={entry.id}
                unknownEntities={unknownEntities}
                isAbleToSync={isAbleToSync}
                vendorName={vendorName}
                customerName={customerName}
                expenseableName={expenseableName}
                serviceName={serviceName}
                description={entry.description}
                date={entry.date}
                amount={entry.amount}
                bill={bill}
                isBillable={isBillable}
                isSynced={isSynced}
                isApprovedForSyncing={isApprovedForSyncing}
                isEditing={isEditing}
                isSelected={isSelected}
                isUpdatingBillable={isUpdatingBillable}
                isUpdatingStatus={isUpdatingStatus}
                attachments={entry.attachments}
                shouldShowQbClasses={shouldShowQbClasses}
                shouldShowBillable={shouldShowBillable}
                onEntrySelect={onEntrySelect.bind(this, entry.id)}
                onBillablePress={this.handleBillablePressed.bind(this, entry)}
                onToggleApprovePress={this.handleToggleApprovePressed.bind(
                    this,
                    entry,
                    noIntegration,
                    pending_override
                )}
                canApprove={canApprove}
                canEdit={canEdit}
                isLocked={isLocked}
                entryOptions={entryOptions}
                noIntegration={noIntegration}
                readOnly={readOnly}
            />
        );
    };

    render() {
        const {
            entriesByWeek,
            weekIds,
            weeklyGrandTotal,
            selectedEntryIds,
            onSelectAllPress,
            currentUser,
            entriesAvailableForEdit,
            showToast,
            readOnly,
        } = this.props;

        const { isMessageModalOpen, isDeleteEntryModalOpen, selectedEntry } = this.state;

        let selectAllIcon = checkboxUnchecked;
        if (selectedEntryIds.length === entriesAvailableForEdit.length) {
            selectAllIcon = checkboxChecked;
        } else if (selectedEntryIds.length > 0) {
            selectAllIcon = checkboxIndeterminate;
        }

        let totalEntries = 0;
        const weeks = weekIds.map(timestamp => {
            const startOfWeek = moment(parseInt(timestamp, 10));
            totalEntries += entriesByWeek[timestamp].length;

            return {
                id: timestamp,
                startOfWeek,
                label: startOfWeek.format('MMM D, YYYY'),
                entries: entriesByWeek[timestamp],
            };
        });

        if (totalEntries > 0) {
            return (
                <div>
                    <table className="dataTable">
                        <thead>
                            <tr className="detailsHeaderRow">
                                <th className="eSelectionCell">
                                    {!readOnly && (
                                        <Icon
                                            className="icon"
                                            icon={selectAllIcon}
                                            size={18}
                                            onClick={onSelectAllPress}
                                        />
                                    )}
                                </th>
                                <th className="eVendorCell">Vendor</th>
                                <th className="eCustomerJobDescriptionCell">
                                    Customer &amp; Job Details
                                </th>
                                <th className="eDateCell">Date</th>
                                <th className="eAmountCell">Amount</th>
                                {!this.props.readOnly && <th className="eStatusCell">&nbsp;</th>}
                                <th className="eTotalAmountCell">
                                    Total: ${weeklyGrandTotal.sum.toFixed(2)}
                                </th>
                            </tr>
                        </thead>
                        {_.map(weeks, week => {
                            return (
                                <tbody key={week.id}>
                                    <tr className="tsWeekHeader">
                                        <td />
                                        <td />
                                        <td>Week of {week.label}</td>
                                        <td />
                                        <td />
                                        <td />
                                        {!this.props.readOnly && <td />}
                                    </tr>
                                    {_.map(week.entries, this.rowForEntry)}
                                </tbody>
                            );
                        })}
                    </table>
                    <DeleteEntryModal
                        isDeleteEntryModalOpen={isDeleteEntryModalOpen}
                        entry={selectedEntry}
                        objectName="ExpenseEntry"
                        requestClose={this.handleDeleteEntryModalClosed}
                        deleteEntry={this.handleDeleteConfirmed}
                        showToast={showToast}
                    />
                    <MessageModal
                        fromEmail={currentUser.email}
                        isMessageModalOpen={isMessageModalOpen}
                        requestClose={this.handleMessageModalClosed}
                        entry={selectedEntry}
                        objectName="ExpenseEntry"
                        showToast={showToast}
                    />
                </div>
            );
        } else {
            return <NoEntriesFound type="Expenses" />;
        }
    }
}

const mapStateToProps = ({ accountUserStore, expensesStore }) => {
    return {
        currentUser: accountUserStore,
        updatingBillableEntryIds: expensesStore.updatingBillableEntryIds,
        updatingStatusEntryIds: expensesStore.updatingStatusEntryIds,
    };
};

const mapDispatchToProps = {
    showToast,
    deleteExpenseEntries,
    setExpenseEntriesBillable,
    setExpenseEntriesApproved,
    setExpenseEntriesLocked,
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ExpenseDetails);
